import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  input,
  viewChild,
  Signal,
  InputSignal,
  InputSignalWithTransform,
  OnDestroy,
  signal,
  WritableSignal,
  inject,
  ElementRef,
  computed,
  effect,
  untracked
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MAT_FORM_FIELD, MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subject } from 'rxjs';

import { IconFlagComponent } from '../icon/icon-flag.component';
import { NewValueAccessor } from '../value-accessor/new-value-accessor';
import { NumberInputDirective } from '../input/number-input.directive';

export class MaterialInputCurrency {
  constructor(
    public amount: string,
    public currency: string
  ) {}
}

@Component({
  selector: 'td-wb-input-currency-selector',
  templateUrl: 'input-currency-selector.component.html',
  styleUrl: 'input-currency-selector.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    IconFlagComponent,
    NumberInputDirective
  ],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: InputCurrencySelectorComponent
    }
  ],
  host: {
    '[attr.id]': 'id',
    '[class.td-wb-input-currency-selector]': 'true',
    '[class.td-wb-input-currency-selector--dropdown-closed]': '!onCurrencySelectOpen()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputCurrencySelectorComponent extends NewValueAccessor<MaterialInputCurrency> implements MatFormFieldControl<MaterialInputCurrency>, OnDestroy {
  static nextId: number = 0;
  readonly showErrorBeforeSubmit: InputSignal<boolean> = input<boolean>(true);
  readonly codes: InputSignal<Array<string>> = input(['CAD', 'USD']);
  readonly size: InputSignal<string> = input<string>('16x11');
  readonly numberInputFormat: InputSignal<string> = input<string>('n4-2');
  readonly numberInputGroupSeparator: InputSignal<boolean> = input<boolean>(true);
  readonly inputName: InputSignal<string> = input<string>('td-wb-input-currency-selector');
  readonly _placeholder: InputSignal<string> = input<string>('', { alias: 'placeholder' });
  readonly amountValidators: InputSignal<Array<ValidatorFn>> = input<Array<ValidatorFn>>([]);

  readonly _required: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute
  });

  readonly _disabledByInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute
  });

  readonly areaInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('amount');

  readonly parts: FormGroup<{
    amount: FormControl<string | null>;
    currency: FormControl<string | null>;
  }>;

  protected readonly formField: MatFormField | null = inject(MAT_FORM_FIELD, {
    optional: true
  });

  readonly onCurrencySelectOpen: WritableSignal<boolean> = signal(false);
  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly touched: WritableSignal<boolean> = signal(false);
  readonly describedbyIds: WritableSignal<string | null> = signal(null);
  readonly id: string = `td-wb-input-currency-selector-${InputCurrencySelectorComponent.nextId++}`;
  private readonly _focused: WritableSignal<boolean> = signal(false);
  private readonly _disabled: Signal<boolean> = computed(() => this._disabledByInput() || this._disabledByCva());
  private readonly _focusMonitor: FocusMonitor = inject(FocusMonitor);
  private readonly _elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly parentForm: NgForm | null = inject(NgForm, { optional: true });
  private readonly parentFormGroup: FormGroupDirective | null = inject(FormGroupDirective, { optional: true });

  get shouldLabelFloat(): boolean { return true; }
  get focused(): boolean { return this._focused(); }
  get empty(): boolean { return false; }
  get placeholder(): string { return this._placeholder(); }
  get required(): boolean { return this._required(); }
  get disabled(): boolean { return this._disabled(); }

  get errorState(): boolean {
    const parent = this.parentForm || this.parentFormGroup;
    return this.parts.invalid &&
           ((this.touched() && this.showErrorBeforeSubmit() || parent?.submitted));
  }

  constructor() {
    super();

    this.parts = inject(FormBuilder).group({
      amount: ['', this.amountValidators()],
      currency: [this.codes()[0] ?? '']
    });

    effect(() => {
      const required = this._required();
      let validators = this.amountValidators();
      if (required) {
        validators = [Validators.required, ...validators];
      }
      this.parts.controls.amount.setValidators(validators);
      this.parts.controls.amount.updateValueAndValidity();
    });

    effect(() => {
      this._placeholder();
      this._required();
      this._disabled();
      this._focused();
      untracked(() => this.stateChanges.next());
    });

    effect(() => {
      if (this._disabled()) {
        untracked(() => this.parts.disable());
      } else {
        untracked(() => this.parts.enable());
      }
    });

    this.parts.statusChanges.pipe(takeUntilDestroyed()).subscribe((): void => {
      this.stateChanges.next();
    });

    this.parts.valueChanges.pipe(takeUntilDestroyed()).subscribe((): void => {
      const value = this.parts.valid
        ? new MaterialInputCurrency(
          this.parts.value.amount || '',
          this.parts.value.currency || ''
        )
        : null;
      this._updateValue(value);
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onFocusIn(): void {
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched.set(true);
      this._focused.set(false);
      this.onTouchedCallback();
    }
  }

  setDescribedByIds(ids: Array<string>): void {
    this.describedbyIds.set(ids.join(' '));
  }

  onContainerClick(): void {
    if (!this.onCurrencySelectOpen()) {
      this._focusMonitor.focusVia(this.areaInput(), 'program');
    }
  }

  _handleInput(): void {
    this.onChangeCallback(this.value);
  }

  onCurrencySelectOpenedChange(event: boolean): void {
    this.onCurrencySelectOpen.set(event);
  }

  private _updateValue(value: MaterialInputCurrency | null): void {
    const current = this.value;
    if (
      value === current ||
      (value?.amount === current?.amount &&
        value?.currency === current?.currency)
    ) {
      return;
    }
    this.value = value;
  }
}
